import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { getDashboardCoffinOutFeventStats } from '@shared/api/giftOrder/giftOrder.controller';
import { CoffinOutFeventStatRow } from '@shared/api/giftOrder/giftOrderDashboard.interface';

import {
  CenterLoading,
  ErrorBox,
  TD_CLASS,
  TH_CLASS,
} from '../../components/DashboardShared';
import {
  addMonths,
  daysInMonth,
  fmt,
  fmtWonCompact,
  getNiceScale,
  linReg,
  parseYm,
  pearsonR,
  rStrengthLabel,
} from './analysisUtils';

/** coffin-out-fevent-stats 미배포 서버는 404 — 장애가 아니라 '미배포'다. */
const isNotFound = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 404;

const WINDOW_OPTIONS: { key: 1 | 3; label: string }[] = [
  { key: 1, label: '이번 달' },
  { key: 3, label: '최근 3개월' },
];

/**
 * 산점도로 볼 관계 프리셋. y=0 상가가 많아(zero-inflated) 회귀선·"실적 상가만" r은
 * y>0 부분집합으로 계산한다 — 전체 r은 "하냐 마냐"가, 부분 r은 "하면 얼마나"가 지배.
 */
interface Relation {
  key: string;
  label: string;
  x: (f: CoffinOutFeventStatRow) => number;
  xUnit: string;
  xTickFmt: (v: number) => string;
  y: (f: CoffinOutFeventStatRow) => number;
  yUnit: string;
  yTickFmt: (v: number) => string;
  /** 회귀 기울기 문구: (slope) => "화환 1건당 답례품 약 n원" */
  slopeText: (slope: number) => string;
  /** y>0 부분집합의 이름 (범례·r 라벨) */
  activeLabel: string;
}

const RELATIONS: Relation[] = [
  {
    key: 'wreathCount-gift',
    label: '화환 건수 → 답례품',
    x: (f) => f.wreathCount,
    xUnit: '화환 건수(건)',
    xTickFmt: fmt,
    y: (f) => f.giftAmount,
    yUnit: '답례품 금액(원)',
    yTickFmt: fmtWonCompact,
    slopeText: (s) => `화환 1건당 답례품 약 ${fmtWonCompact(Math.round(s))}원`,
    activeLabel: '답례품 구매 상가',
  },
  {
    key: 'wreathAmount-gift',
    label: '화환 금액 → 답례품',
    x: (f) => f.wreathAmount,
    xUnit: '화환 금액(원)',
    xTickFmt: fmtWonCompact,
    y: (f) => f.giftAmount,
    yUnit: '답례품 금액(원)',
    yTickFmt: fmtWonCompact,
    slopeText: (s) =>
      `화환 1만원당 답례품 약 ${fmtWonCompact(Math.round(s * 10000))}원`,
    activeLabel: '답례품 구매 상가',
  },
  {
    key: 'share-wreath',
    label: '공유 횟수 → 화환',
    x: (f) => f.shareCount,
    xUnit: '부고 공유 횟수(회)',
    xTickFmt: fmt,
    y: (f) => f.wreathCount,
    yUnit: '화환 건수(건)',
    yTickFmt: fmt,
    slopeText: (s) => `공유 1회당 화환 약 ${s.toFixed(2)}건`,
    activeLabel: '화환 있는 상가',
  },
  {
    key: 'share-gift',
    label: '공유 횟수 → 답례품',
    x: (f) => f.shareCount,
    xUnit: '부고 공유 횟수(회)',
    xTickFmt: fmt,
    y: (f) => f.giftAmount,
    yUnit: '답례품 금액(원)',
    yTickFmt: fmtWonCompact,
    slopeText: (s) => `공유 1회당 답례품 약 ${fmtWonCompact(Math.round(s))}원`,
    activeLabel: '답례품 구매 상가',
  },
];

/** 화환 건수 구간 — "규모 큰 장례일수록 답례품으로 이어지는가"의 실질 답 */
const WREATH_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: '0건', min: 0, max: 0 },
  { label: '1~2건', min: 1, max: 2 },
  { label: '3~5건', min: 3, max: 5 },
  { label: '6~9건', min: 6, max: 9 },
  { label: '10건+', min: 10, max: Infinity },
];

/** 산점도 렌더 상한 — r은 전체로 계산하고 표시만 샘플링 */
const SCATTER_MAX_POINTS = 1500;

interface Props {
  ym: string; // 종료 월 (탭의 월 내비게이션 값)
  brandId: string;
  teamType: string;
}

/**
 * 상가 단위 상관 분석 — "화환·공유가 많은 상가가 답례품도 많이 사는가".
 * 상가 1곳 = 점 1개 (coffin-out-fevent-stats API 원자료).
 *
 * 날짜 단위 상관(상가 많은 날 = 매출 많은 날)은 자명해서 답이 안 된다 —
 * 화환 수·공유 수를 조문 규모의 대리 지표로 두고, 규모가 답례품 구매로
 * 이어지는지를 상가 사이에서 비교한다.
 */
export const CoffinOutCorrelation: FC<Props> = function CoffinOutCorrelation({
  ym,
  brandId,
  teamType,
}) {
  const [months, setMonths] = useState<1 | 3>(3);
  const [relationKey, setRelationKey] = useState(RELATIONS[0]?.key ?? '');
  const [rows, setRows] = useState<CoffinOutFeventStatRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState(false); // 구서버 404

  const relation = RELATIONS.find((r) => r.key === relationKey) ?? RELATIONS[0];

  const fromYm = addMonths(ym, -(months - 1));
  const { y: toY, m: toM } = parseYm(ym);
  const toLastDay = daysInMonth(toY, toM);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotReady(false);
    try {
      const fromIso = new Date(`${fromYm}-01T00:00:00+09:00`).toISOString();
      const toIso = new Date(
        `${ym}-${String(toLastDay).padStart(2, '0')}T23:59:59.999+09:00`,
      ).toISOString();
      const res = await getDashboardCoffinOutFeventStats(
        fromIso,
        toIso,
        brandId || undefined,
        teamType || undefined,
      );
      setRows(res.fevents);
    } catch (e) {
      if (isNotFound(e)) {
        setNotReady(true);
      } else {
        setError(
          e instanceof Error ? e.message : '상관 분석 데이터를 불러올 수 없습니다.',
        );
      }
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [fromYm, ym, toLastDay, brandId, teamType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fevents = useMemo(() => rows ?? [], [rows]);
  const buyers = useMemo(
    () => fevents.filter((f) => f.giftOrderCount > 0),
    [fevents],
  );

  // 선택한 관계의 r — 전체 vs y>0 상가만
  const activeRows = useMemo(
    () => (relation ? fevents.filter((f) => relation.y(f) > 0) : []),
    [fevents, relation],
  );
  const rAll = useMemo(
    () =>
      relation
        ? pearsonR(fevents.map(relation.x), fevents.map(relation.y))
        : null,
    [fevents, relation],
  );
  const rActive = useMemo(
    () =>
      relation
        ? pearsonR(activeRows.map(relation.x), activeRows.map(relation.y))
        : null,
    [activeRows, relation],
  );

  // 구간별 전환율·평균
  const buckets = useMemo(
    () =>
      WREATH_BUCKETS.map((b) => {
        const group = fevents.filter(
          (f) => f.wreathCount >= b.min && f.wreathCount <= b.max,
        );
        const groupBuyers = group.filter((f) => f.giftOrderCount > 0);
        const giftSum = group.reduce((s, f) => s + f.giftAmount, 0);
        return {
          label: b.label,
          feventCount: group.length,
          buyerCount: groupBuyers.length,
          conversion: group.length > 0 ? groupBuyers.length / group.length : 0,
          avgGiftPerFevent:
            group.length > 0 ? Math.round(giftSum / group.length) : 0,
          avgGiftPerBuyer:
            groupBuyers.length > 0
              ? Math.round(giftSum / groupBuyers.length)
              : 0,
        };
      }),
    [fevents],
  );

  // r 요약표 (전체/실적 상가 분리 + 공유 축)
  const pairRows = useMemo(() => {
    const withWreath = fevents.filter((f) => f.wreathCount > 0);
    const defs: {
      label: string;
      group: CoffinOutFeventStatRow[];
      x: (f: CoffinOutFeventStatRow) => number;
      y: (f: CoffinOutFeventStatRow) => number;
    }[] = [
      {
        label: '화환 건수 ↔ 답례품 금액 (전체 상가)',
        group: fevents,
        x: (f) => f.wreathCount,
        y: (f) => f.giftAmount,
      },
      {
        label: '화환 건수 ↔ 답례품 금액 (구매 상가만)',
        group: buyers,
        x: (f) => f.wreathCount,
        y: (f) => f.giftAmount,
      },
      {
        label: '화환 금액 ↔ 답례품 금액 (구매 상가만)',
        group: buyers,
        x: (f) => f.wreathAmount,
        y: (f) => f.giftAmount,
      },
      {
        label: '공유 횟수 ↔ 화환 건수 (전체 상가)',
        group: fevents,
        x: (f) => f.shareCount,
        y: (f) => f.wreathCount,
      },
      {
        label: '공유 횟수 ↔ 화환 건수 (화환 있는 상가만)',
        group: withWreath,
        x: (f) => f.shareCount,
        y: (f) => f.wreathCount,
      },
      {
        label: '공유 횟수 ↔ 답례품 금액 (전체 상가)',
        group: fevents,
        x: (f) => f.shareCount,
        y: (f) => f.giftAmount,
      },
      {
        label: '화환 건수 ↔ 답례품 수량 (구매 상가만)',
        group: buyers,
        x: (f) => f.wreathCount,
        y: (f) => f.giftQuantity,
      },
    ];
    return defs.map((d) => ({
      label: d.label,
      n: d.group.length,
      r: pearsonR(d.group.map(d.x), d.group.map(d.y)),
    }));
  }, [fevents, buyers]);

  const windowToggle = (
    <div className="flex gap-1">
      {WINDOW_OPTIONS.map((w) => (
        <button
          key={w.key}
          onClick={() => setMonths(w.key)}
          className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            months === w.key
              ? 'border-indigo-600 bg-indigo-600 text-white'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );

  if (loading && !rows) return <CenterLoading />;
  if (notReady) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-800">
        상가 단위 통계 API가 아직 이 서버에 배포되지 않았습니다. 배포 후 다시
        열어주세요.
      </div>
    );
  }
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!rows || !relation) return null;

  const convRate = fevents.length > 0 ? buyers.length / fevents.length : 0;
  const sharedCount = fevents.filter((f) => f.shareCount > 0).length;

  return (
    <div className={`space-y-6 ${loading ? 'pointer-events-none opacity-60' : ''}`}>
      {/* 헤더 + 요약 */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              화환·공유가 많은 상가가 답례품도 많이 사는가 (상가 단위)
            </h3>
            <p className="mt-0.5 text-xs text-gray-400">
              {fromYm}-01 ~ {ym}-{String(toLastDay).padStart(2, '0')} 발인 상가{' '}
              {fmt(fevents.length)}곳 · 상가 1곳 = 점 1개
            </p>
          </div>
          {windowToggle}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div>
            <p className="text-xs text-gray-500">발인 상가</p>
            <p className="mt-0.5 text-xl font-bold text-gray-900">
              {fmt(fevents.length)}곳
            </p>
            <p className="text-xs text-gray-400">
              공유 상가 {fmt(sharedCount)}곳 (
              {fevents.length > 0
                ? ((sharedCount / fevents.length) * 100).toFixed(1)
                : '0'}
              %)
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">답례품 구매 상가</p>
            <p className="mt-0.5 text-xl font-bold text-indigo-600">
              {fmt(buyers.length)}곳
            </p>
            <p className="text-xs text-gray-400">
              전환율 {(convRate * 100).toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">r · {relation.label} (전체)</p>
            <p className="mt-0.5 text-xl font-bold text-gray-900">
              {rAll === null ? '—' : rAll.toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">
              r · {relation.activeLabel}만
            </p>
            <p className="mt-0.5 text-xl font-bold text-gray-900">
              {rActive === null ? '—' : rActive.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {fevents.length < 3 ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 shadow">
          상관을 계산하기엔 상가가 너무 적습니다 (3곳 이상 필요).
        </div>
      ) : (
        <>
          {/* 구간별 전환율 — 이 화면의 실질 답 */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h3 className="text-sm font-semibold text-gray-800">
              화환 규모 구간별 답례품 구매
            </h3>
            <p className="mt-0.5 mb-4 text-xs text-gray-400">
              상관계수보다 실질적인 답 — 화환이 많은 구간일수록 전환율·구매액이
              올라가면 "규모가 답례품으로 이어진다"는 뜻
            </p>

            {/* 전환율 막대 */}
            <div className="flex h-36 items-end justify-around gap-2">
              {buckets.map((b) => {
                const maxConv = Math.max(...buckets.map((x) => x.conversion), 0.01);
                const pct = (b.conversion / maxConv) * 100;
                return (
                  <div
                    key={b.label}
                    className="group relative flex h-full flex-1 flex-col items-center justify-end"
                  >
                    <div className="pointer-events-none absolute -top-1 z-10 hidden -translate-y-full rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight whitespace-nowrap text-white shadow group-hover:block">
                      화환 {b.label} 상가 {fmt(b.feventCount)}곳 · 구매{' '}
                      {fmt(b.buyerCount)}곳 ({(b.conversion * 100).toFixed(1)}%)
                      · 구매 상가당 {fmt(b.avgGiftPerBuyer)}원
                    </div>
                    <span className="mb-1 text-[11px] font-bold text-gray-700">
                      {b.feventCount > 0
                        ? `${(b.conversion * 100).toFixed(1)}%`
                        : ''}
                    </span>
                    <div
                      className="w-full max-w-[64px] rounded-t bg-indigo-500 transition-all duration-500"
                      style={{
                        height: `${b.feventCount > 0 ? pct : 0}%`,
                        minHeight: b.conversion > 0 ? 4 : 0,
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-1 flex justify-around gap-2">
              {buckets.map((b) => (
                <span
                  key={b.label}
                  className="flex-1 text-center text-[10px] leading-tight text-gray-500"
                >
                  {b.label}
                  <span className="block text-gray-400">
                    {fmt(b.feventCount)}곳
                  </span>
                </span>
              ))}
            </div>

            {/* 구간 상세 표 */}
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={TH_CLASS}>화환 규모</th>
                    <th className={`${TH_CLASS} text-right`}>상가</th>
                    <th className={`${TH_CLASS} text-right`}>구매 상가</th>
                    <th className={`${TH_CLASS} text-right`}>전환율</th>
                    <th className={`${TH_CLASS} text-right`}>상가당 답례품</th>
                    <th className={`${TH_CLASS} text-right`}>구매 상가당</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {buckets.map((b) => (
                    <tr key={b.label} className="hover:bg-gray-50">
                      <td className={`${TD_CLASS} font-medium`}>{b.label}</td>
                      <td className={`${TD_CLASS} text-right`}>
                        {fmt(b.feventCount)}곳
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        {fmt(b.buyerCount)}곳
                      </td>
                      <td className={`${TD_CLASS} text-right font-bold`}>
                        {b.feventCount > 0
                          ? `${(b.conversion * 100).toFixed(1)}%`
                          : '—'}
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        {b.feventCount > 0 ? `${fmt(b.avgGiftPerFevent)}원` : '—'}
                      </td>
                      <td className={`${TD_CLASS} text-right text-gray-500`}>
                        {b.buyerCount > 0 ? `${fmt(b.avgGiftPerBuyer)}원` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 산점도 */}
          <div className="rounded-lg bg-white p-6 shadow">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  상가별 산점도 — {relation.label}
                </h3>
                <p className="mt-0.5 text-xs text-gray-400">
                  y=0 줄의 회색 점이 "규모는 있는데 실적이 없는 상가" — 회귀선은{' '}
                  {relation.activeLabel}만으로 그린다
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                {RELATIONS.map((rel) => (
                  <button
                    key={rel.key}
                    onClick={() => setRelationKey(rel.key)}
                    className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                      relation.key === rel.key
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {rel.label}
                  </button>
                ))}
              </div>
            </div>
            <FeventScatter
              fevents={fevents}
              activeRows={activeRows}
              relation={relation}
              rAll={rAll}
              rActive={rActive}
            />
          </div>

          {/* r 요약표 */}
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="border-b px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-800">
                지표쌍 상관계수 (피어슨 r, 상가 단위)
              </h3>
              <p className="mt-0.5 text-xs text-gray-400">
                "전체 상가" r은 실적 유무와 크기가 섞인 값 — 크기가 규모를
                따라가는지는 "…만" r을 보세요 · |r| 0.8↑ 매우 강함 / 0.6↑ 강함 /
                0.4↑ 보통 / 0.2↑ 약함
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={TH_CLASS}>지표쌍</th>
                    <th className={`${TH_CLASS} text-right`}>n(상가)</th>
                    <th className={`${TH_CLASS} text-right`}>r</th>
                    <th className={TH_CLASS}>해석</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pairRows.map((row) => (
                    <tr key={row.label} className="hover:bg-gray-50">
                      <td className={`${TD_CLASS} font-medium`}>{row.label}</td>
                      <td className={`${TD_CLASS} text-right text-gray-500`}>
                        {fmt(row.n)}
                      </td>
                      <td className={`${TD_CLASS} text-right font-bold`}>
                        {row.r === null ? '—' : row.r.toFixed(3)}
                      </td>
                      <td className={TD_CLASS}>
                        {row.r === null ? (
                          <span className="text-gray-400">표본 부족</span>
                        ) : (
                          <RBadge r={row.r} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── 내부 컴포넌트 ───────────────────────────────────

const RBadge: FC<{ r: number }> = ({ r }) => {
  const strong = Math.abs(r) >= 0.6;
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
        strong ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {rStrengthLabel(r)}
      {r < 0 && ' (역방향)'}
    </span>
  );
};

/**
 * 상가 1곳 = 점 1개 산점도. r은 전체 데이터로 계산하고, 점이 너무 많으면
 * 표시만 등간격 샘플링한다. 회귀선은 y>0 상가만으로 적합 —
 * y=0 무더기가 기울기를 끌어내리는 걸 막는다.
 */
const FeventScatter: FC<{
  fevents: CoffinOutFeventStatRow[];
  activeRows: CoffinOutFeventStatRow[];
  relation: Relation;
  rAll: number | null;
  rActive: number | null;
}> = ({ fevents, activeRows, relation, rAll, rActive }) => {
  const W = 640;
  const H = 320;
  const ML = 56;
  const MB = 34;
  const MT = 10;
  const MR = 14;
  const plotW = W - ML - MR;
  const plotH = H - MT - MB;

  const xScale = getNiceScale(Math.max(...fevents.map(relation.x), 1), 4);
  const yScale = getNiceScale(Math.max(...fevents.map(relation.y), 1), 4);
  const px = (v: number) => ML + (v / xScale.niceMax) * plotW;
  const py = (v: number) => MT + plotH - (v / yScale.niceMax) * plotH;

  // 표시 샘플링 (계산은 전체 기준)
  const shown = useMemo(() => {
    if (fevents.length <= SCATTER_MAX_POINTS) return fevents;
    const step = fevents.length / SCATTER_MAX_POINTS;
    const out: CoffinOutFeventStatRow[] = [];
    for (let i = 0; i < fevents.length; i += step) {
      const f = fevents[Math.floor(i)];
      if (f) out.push(f);
    }
    return out;
  }, [fevents]);

  // 회귀선 — y>0 상가만으로 적합
  const reg = useMemo(
    () =>
      linReg(activeRows.map(relation.x), activeRows.map(relation.y)),
    [activeRows, relation],
  );
  const regLine = useMemo(() => {
    if (!reg || activeRows.length < 3) return null;
    const ax = activeRows.map(relation.x);
    const x0 = Math.min(...ax);
    const x1 = Math.max(...ax);
    if (x0 === x1) return null;
    const y0 = reg.intercept + reg.slope * x0;
    const y1 = reg.intercept + reg.slope * x1;
    let tMin = 0;
    let tMax = 1;
    if (y1 !== y0) {
      const tA = (0 - y0) / (y1 - y0);
      const tB = (yScale.niceMax - y0) / (y1 - y0);
      tMin = Math.max(0, Math.min(tA, tB));
      tMax = Math.min(1, Math.max(tA, tB));
    } else if (y0 < 0 || y0 > yScale.niceMax) {
      return null;
    }
    if (tMin >= tMax) return null;
    const at = (t: number) => ({
      x: x0 + (x1 - x0) * t,
      y: y0 + (y1 - y0) * t,
    });
    return { a: at(tMin), b: at(tMax) };
  }, [reg, activeRows, relation, yScale.niceMax]);

  return (
    <div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-gray-500">
        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-bold text-gray-700">
          r(전체) = {rAll === null ? '—' : rAll.toFixed(2)}
        </span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-bold text-gray-700">
          r({relation.activeLabel}만) = {rActive === null ? '—' : rActive.toFixed(2)}
        </span>
        {reg && activeRows.length >= 3 && (
          <span>
            {relation.activeLabel} 기준: {relation.slopeText(reg.slope)}
          </span>
        )}
        {fevents.length > SCATTER_MAX_POINTS && (
          <span>
            점 표시는 {fmt(SCATTER_MAX_POINTS)}곳 샘플 (계산은 전체{' '}
            {fmt(fevents.length)}곳)
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="mx-auto mt-1 w-full max-w-3xl min-w-[480px]"
        >
          {yScale.ticks.map((t) => (
            <g key={`y${t}`}>
              <line
                x1={ML}
                x2={W - MR}
                y1={py(t)}
                y2={py(t)}
                stroke="#f3f4f6"
                strokeWidth={1}
              />
              <text
                x={ML - 6}
                y={py(t)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={10}
                fill="#9ca3af"
              >
                {relation.yTickFmt(t)}
              </text>
            </g>
          ))}
          {xScale.ticks.map((t) => (
            <g key={`x${t}`}>
              <line
                x1={px(t)}
                x2={px(t)}
                y1={MT}
                y2={MT + plotH}
                stroke="#f3f4f6"
                strokeWidth={1}
              />
              <text
                x={px(t)}
                y={H - MB + 13}
                textAnchor="middle"
                fontSize={10}
                fill="#9ca3af"
              >
                {relation.xTickFmt(t)}
              </text>
            </g>
          ))}
          <line
            x1={ML}
            x2={W - MR}
            y1={MT + plotH}
            y2={MT + plotH}
            stroke="#d1d5db"
            strokeWidth={1}
          />
          <line
            x1={ML}
            x2={ML}
            y1={MT}
            y2={MT + plotH}
            stroke="#d1d5db"
            strokeWidth={1}
          />
          <text
            x={ML + plotW / 2}
            y={H - 4}
            textAnchor="middle"
            fontSize={10}
            fill="#9ca3af"
          >
            {relation.xUnit}
          </text>
          <text
            x={12}
            y={MT + plotH / 2}
            textAnchor="middle"
            fontSize={10}
            fill="#9ca3af"
            transform={`rotate(-90 12 ${MT + plotH / 2})`}
          >
            {relation.yUnit}
          </text>
          {regLine && (
            <line
              x1={px(regLine.a.x)}
              y1={py(regLine.a.y)}
              x2={px(regLine.b.x)}
              y2={py(regLine.b.y)}
              stroke="#6366f1"
              strokeWidth={1.5}
              strokeOpacity={0.7}
            />
          )}
          {shown.map((f, i) => {
            const active = relation.y(f) > 0;
            return (
              <circle
                key={i}
                cx={px(relation.x(f))}
                cy={py(relation.y(f))}
                r={3}
                fill={active ? '#6366f1' : '#9ca3af'}
                fillOpacity={active ? 0.45 : 0.25}
              >
                <title>
                  {`공유 ${fmt(f.shareCount)}회 · 화환 ${fmt(f.wreathCount)}건(${fmt(f.wreathAmount)}원) · 답례품 ${fmt(f.giftAmount)}원(${fmt(f.giftQuantity)}개)`}
                </title>
              </circle>
            );
          })}
        </svg>
      </div>
      <div className="mt-1 flex justify-end gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-indigo-500 opacity-60" />
          {relation.activeLabel}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-gray-400 opacity-50" />
          실적 없는 상가
        </span>
      </div>
    </div>
  );
};
